import React, { useState, useEffect } from 'react';
import { Header, Footer, MobileBottomNav } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ticketsApi } from '../services/api';
import { toast } from 'sonner';
import { MessageSquare, Plus, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = async () => {
    try {
      const { data } = await ticketsApi.getMy();
      setTickets(data || []);
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error('Please fill in all fields'); return; }
    setSubmitting(true);
    try {
      const { data } = await ticketsApi.create(form);
      toast.success(`Ticket ${data.ticket_number} created!`);
      setShowCreate(false);
      setForm({ subject: '', category: 'general', message: '' });
      fetchTickets();
    } catch (e) {
      toast.error('Failed to create ticket');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0 overflow-x-hidden">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-3 sm:px-6 py-6 w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }}>
              My <span className="text-[#D4AF37]">Tickets</span>
            </h1>
            <p className="text-gray-400 text-sm">Submit disputes or billing queries</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="btn-primary text-sm" data-testid="create-ticket-btn">
            <Plus className="h-4 w-4 mr-1" /> New Ticket
          </Button>
        </div>

        {/* Create Ticket Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="card-dark w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>Create Ticket</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-white text-sm font-semibold">Category</Label>
                  <div className="flex gap-2 mt-1">
                    {['dispute', 'billing', 'general'].map(c => (
                      <button key={c} type="button" onClick={() => setForm({...form, category: c})}
                        className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold capitalize ${form.category === c ? 'bg-[#D4AF37] text-[#1A1A1A] border-[#D4AF37]' : 'bg-[#2D2D2D] text-white border-[#3D3D3D]'}`}
                        data-testid={`ticket-cat-${c}`}
                      >{c}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-white text-sm font-semibold">Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="Brief description of your issue" className="input-dark mt-1" data-testid="ticket-subject" />
                </div>
                <div>
                  <Label className="text-white text-sm font-semibold">Message</Label>
                  <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Describe your issue in detail..." className="input-dark mt-1 w-full min-h-[120px] resize-none" maxLength={1000} data-testid="ticket-message" />
                </div>
                <div className="flex gap-2">
                  <Button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary flex-1" disabled={submitting} data-testid="submit-ticket">
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tickets List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="card-dark text-center py-12">
            <MessageSquare className="h-12 w-12 mx-auto text-gray-500 mb-4" />
            <h3 className="font-bold text-lg text-white mb-2">No tickets yet</h3>
            <p className="text-gray-400 mb-4">Create a ticket if you have a dispute or billing issue</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((t) => (
              <div key={t.id} className="card-dark" data-testid={`ticket-${t.ticket_number}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-[#D4AF37] font-bold text-sm">{t.ticket_number}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold capitalize ${
                      t.category === 'dispute' ? 'bg-red-500/20 text-red-400' :
                      t.category === 'billing' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>{t.category}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    t.status === 'open' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                    t.status === 'resolved' ? 'bg-[#28A745]/20 text-[#28A745]' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>{t.status}</span>
                </div>
                <h3 className="font-bold text-white mb-1">{t.subject}</h3>
                <p className="text-sm text-gray-400 mb-2">{t.message}</p>
                {t.admin_response && (
                  <div className="bg-[#28A745]/10 border border-[#28A745]/30 rounded-lg p-3 mt-2">
                    <div className="text-xs text-[#28A745] font-bold mb-1">Admin Response:</div>
                    <p className="text-sm text-white">{t.admin_response}</p>
                  </div>
                )}
                <div className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(t.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default TicketsPage;
